// Phase 10A — 8 hand-tuned accent presets.
//
// Each preset has TWO seed triplets — one for light mode, one for dark.
// We don't algorithmically derive the dark from light because the
// perceptual jump from copper-on-paper to copper-on-aubergine isn't
// pure inversion; designers tune dark mode for the chroma + lightness
// that reads cleanly against `hsl(280 12% 8%)`.
//
// All 8 presets are pre-verified WCAG AA at 4.5:1 against the canvas
// surfaces (tested in tests/themePresets.test.ts):
//   light canvas: hsl(36 35% 94%)
//   dark canvas:  hsl(280 12% 8%)
//
// The "copper" preset's light values match the existing
// --color-accent / --color-accent-hover / --color-accent-soft in
// tokens.css. Setting accent="copper" should be a no-op visually.
//
// Recovery-context naming: names lean "feels like" not "rewards" —
// avoiding any implication that themes unlock at milestones. Plum is
// the aubergine sibling of the existing --color-primary so users who
// want monochromatic harmony with the primary surfaces (CrisisBanner,
// CrisisOverlay) have an opinionated option.

import type { HSL } from './contrastRatio';

export interface AccentPreset {
  key: AccentPresetKey;
  name: string;
  description: string;
  light: HSL;
  dark: HSL;
}

export type AccentPresetKey =
  | 'copper'
  | 'forest'
  | 'slate'
  | 'plum'
  | 'sand'
  | 'sage'
  | 'indigo'
  | 'rose';

export const DEFAULT_ACCENT_PRESET: AccentPresetKey = 'copper';

export const ACCENT_PRESETS: readonly AccentPreset[] = [
  {
    key: 'copper',
    name: 'Copper',
    description: 'Warm oxidized — the original.',
    // Light L darkened 44 → 40 so cream text-on-accent clears the
    // WCAG 1.4.3 4.5:1 minimum (44 sat just under; 40 → 5.34:1).
    light: { h: 18, s: 58, l: 40 },
    dark: { h: 22, s: 68, l: 64 },
  },
  {
    key: 'forest',
    name: 'Forest',
    description: 'Deep, settled green.',
    light: { h: 152, s: 38, l: 32 },
    dark: { h: 150, s: 42, l: 60 },
  },
  {
    key: 'slate',
    name: 'Slate',
    description: 'Cool, quiet blue-gray.',
    light: { h: 210, s: 24, l: 38 },
    dark: { h: 210, s: 36, l: 68 },
  },
  {
    key: 'plum',
    name: 'Plum',
    description: 'Mono with the primary aubergine.',
    light: { h: 320, s: 32, l: 36 },
    dark: { h: 320, s: 42, l: 72 },
  },
  {
    key: 'sand',
    name: 'Sand',
    description: 'Warm earthen neutral.',
    light: { h: 32, s: 42, l: 36 },
    dark: { h: 34, s: 50, l: 64 },
  },
  {
    key: 'sage',
    name: 'Sage',
    description: 'Muted green-gray.',
    light: { h: 130, s: 18, l: 36 },
    dark: { h: 130, s: 24, l: 64 },
  },
  {
    key: 'indigo',
    name: 'Indigo',
    description: 'Calm, classical blue.',
    light: { h: 232, s: 36, l: 42 },
    dark: { h: 232, s: 50, l: 70 },
  },
  {
    key: 'rose',
    name: 'Rose',
    description: 'Soft, desaturated pink-red.',
    light: { h: 348, s: 38, l: 42 },
    dark: { h: 348, s: 50, l: 70 },
  },
];

/** Look up a preset by key. Returns the default ("copper") on unknown. */
export function getAccentPreset(key: string | null | undefined): AccentPreset {
  if (!key) return ACCENT_PRESETS[0];
  const found = ACCENT_PRESETS.find((p) => p.key === key);
  return found ?? ACCENT_PRESETS[0];
}

/** All preset keys, in display order. */
export const ACCENT_PRESET_KEYS: readonly AccentPresetKey[] = ACCENT_PRESETS.map(
  (p) => p.key,
);

// -----------------------------------------------------------------------
// Typography
// -----------------------------------------------------------------------

/**
 * Phase 10A typography stack. Each option maps to a CSS data-attr
 * (`data-font`, `data-font-scale`, `data-leading`) the stylesheet hooks
 * for the actual font-family / size / line-height swap.
 */
export type FontFamilyKey = 'default' | 'system' | 'hyperlegible';
export type FontScaleKey = 'sm' | 'md' | 'lg' | 'xl';
export type LineHeightKey = 'snug' | 'normal' | 'relaxed';

export interface FontFamilyOption {
  key: FontFamilyKey;
  name: string;
  description: string;
}

export interface FontScaleOption {
  key: FontScaleKey;
  name: string;
  multiplier: number;
}

export interface LineHeightOption {
  key: LineHeightKey;
  name: string;
  description: string;
}

export const FONT_FAMILY_OPTIONS: readonly FontFamilyOption[] = [
  {
    key: 'default',
    name: 'Default',
    description: 'Fraunces headings + Inter Tight body.',
  },
  {
    key: 'system',
    name: 'System',
    description: 'Your device font — fastest on older devices.',
  },
  {
    key: 'hyperlegible',
    name: 'Hyperlegible',
    description: 'Atkinson Hyperlegible — easier for low-vision readers.',
  },
];

export const FONT_SCALE_OPTIONS: readonly FontScaleOption[] = [
  { key: 'sm', name: 'Small', multiplier: 0.875 },
  { key: 'md', name: 'Default', multiplier: 1 },
  { key: 'lg', name: 'Large', multiplier: 1.125 },
  { key: 'xl', name: 'Extra large', multiplier: 1.25 },
];

export const LINE_HEIGHT_OPTIONS: readonly LineHeightOption[] = [
  { key: 'snug', name: 'Snug', description: 'Tighter line spacing.' },
  { key: 'normal', name: 'Default', description: 'Standard line height.' },
  {
    key: 'relaxed',
    name: 'Relaxed',
    description: 'More space between lines — easier to scan.',
  },
];

export const DEFAULT_FONT_FAMILY: FontFamilyKey = 'default';
export const DEFAULT_FONT_SCALE: FontScaleKey = 'md';
export const DEFAULT_LINE_HEIGHT: LineHeightKey = 'normal';

export function getFontScaleMultiplier(key: FontScaleKey | null | undefined): number {
  const found = FONT_SCALE_OPTIONS.find((o) => o.key === key);
  return found?.multiplier ?? 1;
}
